import { apiFetch } from './client.ts';
import type { UserRoleType } from './types.ts';

/**
 * MVP-226: the admin account surface (`/api/accounts`) and the invite surface (`/api/invites`),
 * plus the two PUBLIC invite-redemption calls the signed-out redeem page makes.
 *
 * The redeem pair passes `auth: false` deliberately: the caller has no account yet, and a browser
 * that happens to hold an admin's JWT from another tab must not have it attached — Strapi checks a
 * request against the AUTHENTICATED role the moment an `Authorization` header is present and never
 * falls back to what Public would allow (see `ApiFetchOptions.auth`'s own comment), so sending one
 * here would 403 the very person the link is for.
 */

/** The role an invite grants, in the words the screen uses. `authenticated` is Strapi's plumbing
 * name for this product's Customer role — see `api/roles.ts`'s `roleLabel`. */
export type InvitableRole = Extract<
  UserRoleType,
  'authenticated' | 'downloader' | 'admin' | 'shop' | 'agency' | 'rights'
>;

export interface AccountUser {
  id: number;
  documentId: string | null;
  username: string;
  email: string | null;
  displayName: string | null;
  roleType: UserRoleType | null;
  roleName: string | null;
  blocked: boolean;
  confirmed: boolean;
  /** ISO datetimes, or null for "no bound on that side" — an account with neither is permanent. */
  validFrom: string | null;
  validUntil: string | null;
  createdAt: string | null;
}

export interface InviteLink {
  id: number;
  documentId: string;
  roleType: InvitableRole;
  label: string | null;
  validFrom: string | null;
  validUntil: string | null;
  /** When the LINK itself stops working — separate from, and much shorter than, the account window
   * it grants (14 days; `strapi-template/src/utils/invite-view.js`). */
  expiresAt: string | null;
  usedAt: string | null;
  usedByUsername: string | null;
  revokedAt: string | null;
  createdByUsername: string | null;
  createdAt: string | null;
}

export interface AccountPatch {
  roleType?: InvitableRole;
  blocked?: boolean;
  displayName?: string | null;
  /** `null` is meaningful — it is how the screen says "permanent". Omit the key to leave it alone. */
  validFrom?: string | null;
  validUntil?: string | null;
}

/**
 * MVP-510 slice 4: one row of a venue's OWN staff roster (`publicStaffUser` in
 * `strapi-template/src/utils/account-view.js`) — an `AccountUser` plus which branch the person
 * belongs to. Both venue fields are nullable: an account minted before slice 1 carries no venue,
 * and the roster still has to be able to show it rather than hide the row it cannot label.
 */
export interface StaffUser extends AccountUser {
  venueId: number | null;
  venueName: string | null;
}

/**
 * MVP-510 slice 4. Deliberately NOT `AccountPatch`: there is no `roleType`, because the route
 * behind it has no such field (`parseStaffPatch`, same file as above — a shop signs up operators
 * and administers operators, and promotion is not a guard that a body could get round because the
 * field does not exist). Written out rather than `Omit<AccountPatch, 'roleType'>` for the reason
 * the server gives for not delegating: a shape that serves both callers is how a field added for
 * the admin screen silently reaches this one.
 */
export interface StaffPatch {
  blocked?: boolean;
  /** `null` is meaningful — "permanent". Omit the key to leave it alone. */
  validFrom?: string | null;
  validUntil?: string | null;
}

/** MVP-510 slice 4. The venue filter is the SERVER's (`venueIdsForUser`); nothing sent from here
 * chooses whose accounts come back. An admin gets the whole deployment from `listAccounts()`
 * instead — this route answers them too, but that roster is a superset of it. */
export async function listStaff(): Promise<StaffUser[]> {
  const res = await apiFetch<{ data: StaffUser[] }>('/api/accounts/staff');
  return res.data;
}

export async function patchStaff(id: number, patch: StaffPatch): Promise<StaffUser> {
  const res = await apiFetch<{ data: StaffUser }>(`/api/accounts/staff/${id}`, {
    method: 'PATCH',
    body: { data: patch },
    retry: false,
  });
  return res.data;
}

export interface NewInvite {
  roleType: InvitableRole;
  label?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  /**
   * MVP-655: the branch a `shop` invite is for, by slug. A QUERY parameter rather than a body field,
   * and that is the server's shape, not a preference — `invite.create` resolves it through
   * `venueIdForAdminRequest` (`?venue=`), the same helper `room.create` uses, so the venue is
   * authorised against the issuer instead of trusted off the payload. Required for `shop` and
   * refused with `VENUE_REQUIRED` when absent: a shop IS a venue, and a fallback here would plant
   * the new owner in whichever shop is already trading. Ignored by the server for every other role.
   */
  venueSlug?: string | null;
}

export async function listAccounts(): Promise<AccountUser[]> {
  const res = await apiFetch<{ data: AccountUser[] }>('/api/accounts');
  return res.data;
}

export async function patchAccount(id: number, patch: AccountPatch): Promise<AccountUser> {
  const res = await apiFetch<{ data: AccountUser }>(`/api/accounts/${id}`, {
    method: 'PATCH',
    body: { data: patch },
    retry: false,
  });
  return res.data;
}

/** MVP-231: the signed-in person editing their OWN display name. No id anywhere — the server reads
 * the target off the JWT — and `displayName` is the only field this endpoint accepts. */
export async function patchMyProfile(displayName: string | null): Promise<AccountUser> {
  const res = await apiFetch<{ data: AccountUser }>('/api/account/me', {
    method: 'PATCH',
    body: { data: { displayName } },
    retry: false,
  });
  return res.data;
}

export async function listInvites(): Promise<InviteLink[]> {
  const res = await apiFetch<{ data: InviteLink[] }>('/api/invites');
  return res.data;
}

/** The ONLY moment the plaintext token exists. Nothing stores it — the server keeps a SHA-256 — so
 * a caller that loses it has to issue a new link. */
export async function createInvite({
  venueSlug,
  ...invite
}: NewInvite): Promise<{ invite: InviteLink; token: string }> {
  // MVP-655: `venueSlug` leaves the body deliberately — see NewInvite. The rest of the draft is the
  // body verbatim, as it has always been.
  const query = venueSlug ? `?venue=${encodeURIComponent(venueSlug)}` : '';
  const res = await apiFetch<{ data: { invite: InviteLink; token: string } }>(`/api/invites${query}`, {
    method: 'POST',
    body: { data: invite },
    retry: false,
  });
  return res.data;
}

export async function revokeInvite(documentId: string): Promise<InviteLink> {
  const res = await apiFetch<{ data: InviteLink }>(`/api/invites/${documentId}/revoke`, {
    method: 'POST',
    retry: false,
  });
  return res.data;
}

export interface InvitePreview {
  roleType: InvitableRole;
  validFrom: string | null;
  validUntil: string | null;
}

/** PUBLIC. 404 (`INVITE_INVALID` / `INVITE_EXPIRED`) for a token that is unknown, already used or
 * revoked — the server answers all three identically on purpose. */
export async function previewInvite(token: string): Promise<InvitePreview> {
  const res = await apiFetch<{ data: InvitePreview }>(`/api/invites/redeem/${encodeURIComponent(token)}`, { auth: false });
  return res.data;
}

/** MVP-227: one row of the admin audit log. `detail` is deliberately `unknown` — its shape depends
 * on the action, and `features/admin/audit.ts` is the one place that narrows it, so a new action on
 * the server cannot make this module lie about what it holds. */
export interface AuditEvent {
  documentId: string | null;
  /** ISO datetime; the server's `createdAt`, renamed because the event time is the only time. */
  at: string | null;
  action: string;
  actor: string | null;
  actorId: number | null;
  target: string | null;
  detail: unknown;
}

/**
 * MVP-444. One page of the audit log.
 *
 * `total` counts the rows that match the FILTER, not the whole table — a total that ignores the
 * filter describes a list nobody is looking at. `cursor` is opaque: hand it back verbatim for the
 * next page, `null` means that was the last one. `actors`/`actions` arrive on the first page of a
 * filter only, so the dropdowns do not change as you scroll.
 */
export interface AuditPage {
  events: AuditEvent[];
  total: number;
  cursor: string | null;
  hasMore: boolean;
  actors?: string[];
  actions?: string[];
}

export interface AuditQuery {
  cursor?: string | null;
  limit?: number;
  /** One action from the server's closed vocabulary. Anything else is ignored server-side rather
   * than answered with an empty list that reads like "nothing happened". */
  action?: string;
  actor?: string;
}

/** MVP-227. Admin only; newest first. MVP-444 made it cursor-paged and filterable. */
export async function listAuditEvents(query: AuditQuery = {}): Promise<AuditPage> {
  const params = new URLSearchParams({ limit: String(query.limit ?? 50) });
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.action) params.set('action', query.action);
  if (query.actor) params.set('actor', query.actor);
  const res = await apiFetch<{
    data: AuditEvent[];
    meta: { total: number; cursor?: string | null; hasMore?: boolean; actors?: string[]; actions?: string[] };
  }>(`/api/audit-events?${params.toString()}`);
  return {
    events: res.data,
    total: res.meta?.total ?? res.data.length,
    cursor: res.meta?.cursor ?? null,
    hasMore: res.meta?.hasMore ?? false,
    actors: res.meta?.actors,
    actions: res.meta?.actions,
  };
}

/** PUBLIC. Creates the account. The role and window come off the invite row server-side; nothing
 * sent from here can widen either. */
export async function redeemInvite(
  token: string,
  account: { username: string; email: string; password: string; displayName?: string }
): Promise<{ username: string; roleType: InvitableRole }> {
  const res = await apiFetch<{ data: { username: string; roleType: InvitableRole } }>(
    `/api/invites/redeem/${encodeURIComponent(token)}`,
    { method: 'POST', body: { data: account }, retry: false, auth: false }
  );
  return res.data;
}
